import { Box, Divider } from "@mui/material";
import { SolarMenu } from "@react-admin/ra-navigation";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useAppTheme } from "@/hooks/useAppTheme";
import { ResourceName } from "@/types";
import { SearchSubMenu } from "../SearchSubMenu";
import { Search as SearchIcon } from "@mui/icons-material";
import {
  ClientsMenuItem,
  PoliciesMenuItem,
  ClaimantsMenuItem,
  StoplossClaimsMenuItem,
  ReimbursementsMenuItem,
  TasksMenuItem,
  DocumentsMenuItem,
  VendorsMenuItem,
  HelpMenuItem,
  AdminMenuItem,
} from "./SidebarMenuItems";
import { UserProfileMenuItem } from "./UserProfileMenu";

// ─── Main Sidebar Menu ──────────────────────────────────────────────────────

export const SidebarMenu = () => {
  const { theme, isDarkMode, logoImage, logoImageDark } = useAppTheme();

  const clientsPerms = useResourcePermissions(ResourceName.CLIENTS);
  const policiesPerms = useResourcePermissions(ResourceName.POLICIES);
  const claimantsPerms = useResourcePermissions(ResourceName.CLAIMANTS);
  const claimsPerms = useResourcePermissions(ResourceName.STOPLOSS_CLAIMS);
  const reimbursementsPerms = useResourcePermissions(ResourceName.REIMBURSEMENTS);
  const tasksPerms = useResourcePermissions(ResourceName.TASKS);
  const documentsPerms = useResourcePermissions(ResourceName.DOCUMENTS);
  const vendorsPerms = useResourcePermissions(ResourceName.VENDORS);

  return (
    <SolarMenu
      bottomToolbar={<BottomToolbar />}
      sx={{
        "& .RaSolarPrimarySidebar-root .MuiDrawer-paper": {
          backgroundColor: theme.palette.primary.main,
          "& .MuiButtonBase-root": {
            color: theme.palette.primary.contrastText,
          },
          "& .MuiButtonBase-root.Mui-selected": {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
          },
        },
      }}
    >
      <SolarMenu.DashboardItem
        icon={
          <Box
            component="img"
            src={isDarkMode ? logoImageDark || logoImage || "/logo-black.png" : logoImage || "/logo-white.png"}
            alt="Dashboard"
            sx={{ width: 30, height: 30, objectFit: "contain" }}
          />
        }
      />

      {/* Core domain — follows the business hierarchy */}
      {clientsPerms.hasAnyPermission && <ClientsMenuItem />}
      {policiesPerms.hasAnyPermission && <PoliciesMenuItem />}
      {claimantsPerms.hasAnyPermission && <ClaimantsMenuItem />}
      {claimsPerms.hasAnyPermission && <StoplossClaimsMenuItem />}
      {reimbursementsPerms.hasAnyPermission && <ReimbursementsMenuItem />}

      <SidebarDivider />

      {/* Workflow */}
      {tasksPerms.hasAnyPermission && <TasksMenuItem />}
      {documentsPerms.hasAnyPermission && <DocumentsMenuItem />}

      <SidebarDivider />

      {/* Reference */}
      {vendorsPerms.hasAnyPermission && <VendorsMenuItem />}
    </SolarMenu>
  );
};

// ─── Bottom Toolbar ─────────────────────────────────────────────────────────

const BottomToolbar = () => {
  const usersPerms = useResourcePermissions(ResourceName.USERS);
  const helpPerms = useResourcePermissions(ResourceName.HELP_ARTICLES);

  return (
    <>
      {usersPerms.hasAnyPermission && <AdminMenuItem />}
      {helpPerms.hasAnyPermission && <HelpMenuItem />}
      <SolarMenu.Item
        icon={<SearchIcon />}
        label="Search"
        name="search"
        subMenu={<SearchSubMenu />}
        data-testid="search-button"
      />
      <SolarMenu.LoadingIndicatorItem />
      <UserProfileMenuItem />
    </>
  );
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Subtle divider for grouping sidebar icons. Visible on the primary-colored background. */
const SidebarDivider = () => <Divider sx={{ mx: 1, my: 0.5, borderColor: "rgba(255,255,255,0.4)" }} />;
